import type { InterrogationNodeV1, NpcInstanceV1 } from "@cold-case/shared/case-manifest";

import { isPermQuestionId } from "./perm-question-id.js";
import type { CoherenceJournal } from "./dialog-parser.js";
import { substituteDialog } from "./dialog-parser.js";
import {
  answerForInterrogationNode,
  collectCluesForInterrogationNode,
  npcConversationOpener,
} from "./npc-dialog-flow.js";

export type DialogTranscriptLine = { speaker: "npc" | "player"; text: string };

export type NpcDialogSessionPhase = "greeting" | "picking" | "farewell" | "done";

/** Max interrogation choices shown at once (avoids dumping the whole script). */
export const NPC_DIALOG_CHOICES_PER_PAGE = 3;

export type NpcDialogCloseResult = {
  clueIdsToReveal: string[];
  talkedInstanceId: string;
  /** False when the player used Close without advancing the conversation. */
  progressed: boolean;
  /** True when player asked a perm_grant__* question (building clearance). */
  grantedBuildingClearance: boolean;
};

export class NpcDialogSession {
  phase: NpcDialogSessionPhase = "greeting";
  readonly askedQuestionIds = new Set<string>();
  readonly transcript: DialogTranscriptLine[] = [];
  private readonly clueIds = new Set<string>();
  private questionPage = 0;
  private dismissedWithoutProgress = false;
  private askedPermissionGrant = false;

  constructor(
    private readonly npc: NpcInstanceV1,
    private readonly getJournal: () => CoherenceJournal,
    private readonly playerCount: number,
    private readonly names: string[],
    private readonly victimName: string
  ) {}

  private journal(): CoherenceJournal {
    return this.getJournal();
  }

  start(): void {
    this.transcript.push({
      speaker: "npc",
      text: npcConversationOpener(this.npc, this.names, this.victimName),
    });
    this.phase = "greeting";
  }

  /** Player read the opener and is ready to pick a topic. */
  advanceFromGreeting(): void {
    if (this.phase !== "greeting") return;
    if (this.availableQuestions().length === 0) {
      this.enterFarewell();
      return;
    }
    this.phase = "picking";
  }

  availableQuestions(): InterrogationNodeV1[] {
    return (this.npc.dialog.interrogation ?? []).filter(
      (q) => !this.askedQuestionIds.has(q.question_id)
    );
  }

  /** Next page when more than {@link NPC_DIALOG_CHOICES_PER_PAGE} topics remain. */
  showMoreQuestionTopics(): void {
    const pages = Math.ceil(
      this.availableQuestions().length / NPC_DIALOG_CHOICES_PER_PAGE
    );
    if (pages <= 1) return;
    this.questionPage = (this.questionPage + 1) % pages;
  }

  visibleQuestions(): InterrogationNodeV1[] {
    const all = this.availableQuestions();
    const start = this.questionPage * NPC_DIALOG_CHOICES_PER_PAGE;
    return all.slice(start, start + NPC_DIALOG_CHOICES_PER_PAGE);
  }

  hasMoreQuestionPages(): boolean {
    return this.availableQuestions().length > NPC_DIALOG_CHOICES_PER_PAGE;
  }

  endConversationEarly(): void {
    if (this.phase === "greeting" || this.phase === "picking") this.enterFarewell();
  }

  private enterFarewell(): void {
    if (this.phase === "farewell" || this.phase === "done") return;
    this.phase = "farewell";
    const farewell = this.npc.dialog.farewell_lines?.find((s) => String(s).trim().length);
    if (farewell) {
      this.transcript.push({
        speaker: "npc",
        text: substituteDialog(String(farewell), this.names, this.victimName),
      });
    }
    this.phase = "done";
  }

  pickQuestion(questionId: string): void {
    if (this.phase !== "picking") return;
    const node = this.npc.dialog.interrogation?.find((q) => q.question_id === questionId);
    if (!node || this.askedQuestionIds.has(questionId)) return;

    const qText = substituteDialog(String(node.question_prompt ?? ""), this.names, this.victimName);
    this.transcript.push({ speaker: "player", text: qText });
    this.askedQuestionIds.add(questionId);
    if (isPermQuestionId(questionId)) {
      this.askedPermissionGrant = true;
    }

    const { answer, met } = answerForInterrogationNode(node, this.journal(), this.playerCount);
    const aText = substituteDialog(String(answer ?? ""), this.names, this.victimName);
    this.transcript.push({ speaker: "npc", text: aText });
    collectCluesForInterrogationNode(node, met, this.clueIds);

    if (met === false && node.condition?.locked_hint?.trim()) {
      this.transcript.push({
        speaker: "npc",
        text: substituteDialog(node.condition.locked_hint.trim(), this.names, this.victimName),
      });
    }

    this.questionPage = 0;
    if (this.availableQuestions().length === 0) {
      this.enterFarewell();
    }
  }

  /** Exit without farewell — player can talk to this NPC again fresh. */
  dismissWithoutProgress(): void {
    this.dismissedWithoutProgress = true;
  }

  isGreeting(): boolean {
    return this.phase === "greeting";
  }

  canPickQuestions(): boolean {
    return this.phase === "picking" && this.availableQuestions().length > 0;
  }

  isConversationDone(): boolean {
    return this.phase === "done";
  }

  /** Restore transcript/phase for follower mirror view (read-only). */
  applyMirrorState(mirror: {
    phase: NpcDialogSessionPhase;
    transcript: DialogTranscriptLine[];
    questionPage: number;
  }): void {
    this.transcript.length = 0;
    for (const line of mirror.transcript) {
      this.transcript.push({ speaker: line.speaker, text: line.text });
    }
    this.phase = mirror.phase;
    this.questionPage = Math.max(0, mirror.questionPage);
  }

  getQuestionPage(): number {
    return this.questionPage;
  }

  getInstanceId(): string {
    return this.npc.instance_id;
  }

  close(): NpcDialogCloseResult {
    const progressed =
      !this.dismissedWithoutProgress &&
      (this.askedQuestionIds.size > 0 || this.clueIds.size > 0);
    return {
      clueIdsToReveal: progressed ? [...this.clueIds] : [],
      talkedInstanceId: this.npc.instance_id,
      progressed,
      grantedBuildingClearance: this.askedPermissionGrant,
    };
  }
}
